/**
 * 须从本文件同目录的 keyCodeMapper 直引，避免 `import { … } from "@eweadn-qiankun/shared-components"`
 * 在入口先加载 keyCodeMapperBeiying 时形成环，导致 KEYBOARD_LAYOUTS 未初始化就访问。
 */
import { isBeiyingDevice8815X98Keyboard } from "@eweadn-qiankun/shared-utils";
import {
  KEYCODE_MAP,
  KEYBOARD_LAYOUTS,
  KEYCODE_MACRO_MAP,
} from "./keyCodeMapper";
import { M71_FACTORY_POS_BY_KEYCODE } from "./m71FactoryPosFromInitJson";
import { T87_FACTORY_POS_BY_KEYCODE } from "./t87FactoryPosFromInitJson";
import {
  X75V2_FACTORY_POS_BY_KEYCODE,
  X75V2_KNOB_DEVICE_VALUE,
} from "./x75v2FactoryPosFromInitJson";
import {
  M75_FACTORY_POS_BY_KEYCODE,
  M75_KNOB_DEVICE_VALUE,
} from "./m75FactoryPosFromInitJson";

export { X75V2_KNOB_DEVICE_VALUE };
export { M75_KNOB_DEVICE_VALUE };
import {
  type X98_1KKeySlot,
  X98_1K_DEFAULT_POS_BY_KEYCODE,
  BEIYING_8815_LAYER0_KEYBOARD_KEYS,
} from "./x98_1kBeiyingKeyboardRaw";
import { LayerType, SystemType } from "@bytech/web-hid-sdk/keyboard";
// i18next-extract-mark-ns-start keycode-mapper-beiying-dynamic
// Dynamic keys used in this file:
// t("playPause")
// t("systemMultimedia")
// t("muteKey")
// t("pauseMedia")
// t("previousSong")
// t("nextSong")
// t("volumeUp")
// t("volumeDown")
// t("mouseLeft")
// t("mouseRight")
// t("mouseMiddle")
// t("mouseForward")
// t("mouseBack")
// i18next-extract-mark-ns-end keycode-mapper-beiying-dynamic
interface DefaultKeycap {
  /** 通常是物理键帽上的字符 */
  legend: string;
  /** 键码 (4B) */
  keycode: number;
  /** 按键 id (2B) */
  id: number;
  /** 浏览器 KeyboardEvent.code 的值 */
  browserCode: string;
  xsKeycode?: number;
  /** M80 出厂 keycode → 设备键槽 pos（与 `M80_DEFAULT_POS_BY_KEYCODE` 一致，便于命中后直读） */
  m80pos?: number;
}
export interface SDKRowLayout {
  layer: number;
  row: number;
  keyboardLayout: number[];
  keyboardData?: any;
  ledBeads?: any;
}

export interface M80KeySlot {
  keycode: number;
  pos: number;
  row: number;
  col: number;
}

/**
 * M80 出厂键槽映射（来自未改键 getKeyboardKeys 基准数据）：
 * keycode(HID/layout) -> pos(设备键槽)
 */
const M80_DEFAULT_POS_BY_KEYCODE: Record<number, number> = {
  4: 44,
  5: 144,
  6: 96,
  7: 92,
  8: 88,
  9: 116,
  10: 140,
  11: 164,
  12: 208,
  13: 188,
  14: 212,
  15: 236,
  16: 192,
  17: 168,
  18: 232,
  19: 256,
  20: 40,
  21: 112,
  22: 68,
  23: 136,
  24: 184,
  25: 120,
  26: 64,
  27: 72,
  28: 160,
  29: 48,
  30: 36,
  31: 60,
  32: 84,
  33: 108,
  34: 132,
  35: 156,
  36: 180,
  37: 204,
  38: 228,
  39: 252,
  40: 332,
  41: 8,
  42: 324,
  43: 16,
  44: 148,
  45: 276,
  46: 300,
  47: 280,
  48: 304,
  49: 308,
  51: 260,
  52: 284,
  53: 12,
  54: 216,
  55: 240,
  56: 264,
  57: 20,
  58: 32,
  59: 56,
  60: 80,
  61: 104,
  62: 128,
  63: 152,
  64: 176,
  65: 200,
  66: 224,
  67: 248,
  68: 272,
  69: 296,
  70: 320,
  74: 372,
  75: 380,
  76: 368,
  77: 376,
  78: 384,
  79: 388,
  80: 340,
  81: 364,
  82: 360,
  224: 28,
  225: 24,
  226: 76,
  227: 52,
  228: 268,
  229: 312,
  230: 220,
  61697: 244,
};

/**
 * M80(Fn层) 出厂键槽映射（来自未改键 getKeyboardKeys 基准数据）：
 * keycode(HID/layout) -> pos(设备键槽)
 */
const M80_DEFAULT_FN_POS_BY_KEYCODE: Record<number, number> = {
  "4": 117440536,
  "5": 0,
  "6": 0,
  "7": 0,
  "8": 117440519,
  "9": 0,
  "10": 117440522,
  "11": 0,
  "12": 0,
  "13": 0,
  "14": 0,
  "15": 0,
  "16": 0,
  "17": 0,
  "18": 0,
  "19": 0,
  "20": 117440517,
  "21": 117440520,
  "22": 117440538,
  "23": 0,
  "24": 0,
  "25": 0,
  "26": 117440518,
  "27": 0,
  "28": 0,
  "29": 0,
  "30": 0,
  "31": 0,
  "32": 0,
  "33": 0,
  "34": 0,
  "35": 0,
  "36": 0,
  "37": 0,
  "38": 0,
  "39": 0,
  "40": 134348800,
  "41": 0,
  "42": 117440523,
  "43": 117440530,
  "44": 0,
  "45": 0,
  "46": 0,
  "47": 0,
  "48": 0,
  "49": 0,
  "51": 0,
  "52": 0,
  "53": 0,
  "54": 0,
  "55": 0,
  "56": 0,
  "57": 117440516,
  "58": 33554544,
  "59": 33554543,
  "60": 524331,
  "61": 33554834,
  "62": 33554979,
  "63": 33554977,
  "64": 33554614,
  "65": 33554637,
  "66": 33554613,
  "67": 33554658,
  "68": 33554666,
  "69": 33554665,
  "70": 0,
  "74": 73,
  "75": 134217729,
  "76": 72,
  "77": 71,
  "78": 134348801,
  "79": 134480128,
  "80": 134480384,
  "81": 134414848,
  "82": 134414592,
  "224": 0,
  "225": 0,
  "226": 0,
  "227": 117440513,
  "228": 0,
  "229": 0,
  "230": 117440529,
  "61697": 218103808,
};

/**
 * M80 键槽列表：由静态矩阵（row/col）+ 出厂 pos 映射生成。
 * 结构示例：{ keycode: 41, pos: 8, row: 0, col: 0 }
 */
export const M80_KEYS_BEIYING: M80KeySlot[] = (
  (KEYBOARD_LAYOUTS.m80 as number[][]) || []
).flatMap((rowData: number[], row: number) =>
  rowData
    .map((keycode: number, col: number) => {
      if (!keycode) return null;
      const pos = M80_DEFAULT_POS_BY_KEYCODE[keycode];
      if (!Number.isFinite(pos)) return null;
      return { keycode, pos, row, col };
    })
    .filter((item): item is M80KeySlot => Boolean(item))
);

function buildBeiyingKeycapTryKeyOrder(k: DefaultKeycap): number[] {
  const out: number[] = [];
  if (typeof k.keycode === "number" && Number.isFinite(k.keycode)) {
    out.push(k.keycode);
  }
  if (typeof k.xsKeycode === "number" && Number.isFinite(k.xsKeycode)) {
    out.push(k.xsKeycode);
  }
  return out;
}

/** 与 `M80_DEFAULT_POS_BY_KEYCODE` 的查找顺序：主 keycode → xsKeycode */
function resolveM80PosForKeycap(k: DefaultKeycap): number | undefined {
  for (const t of buildBeiyingKeycapTryKeyOrder(k)) {
    const p = M80_DEFAULT_POS_BY_KEYCODE[t];
    if (Number.isFinite(p)) return p;
  }
  return undefined;
}
function resolveM80FnPosForKeycap(k: DefaultKeycap): number | undefined {
  for (const t of buildBeiyingKeycapTryKeyOrder(k)) {
    const p = M80_DEFAULT_FN_POS_BY_KEYCODE[t];
    if (Number.isFinite(p)) return p;
  }
  return undefined;
}

/**
 * @bytech/web-hid-sdk 虚拟键盘 / getKeymap(s) 按键 id↔HID↔文案 — 与 8815 工厂 JSON 无关。
 * 8815 专用数据见 `BEIYING_8815_LAYER0_KEYBOARD_KEYS`（x98_1kBeiyingKeyboardRaw.ts）。
 */
export const KEYCODE_MAP_BEIYING_RAW: DefaultKeycap[] = [
  {
    legend: "Esc",
    keycode: 41,
    id: 1,
    browserCode: "Escape",
  },
  {
    legend: "F1",
    keycode: 58,
    id: 2,
    browserCode: "F1",
  },
  {
    legend: "F2",
    keycode: 59,
    id: 3,
    browserCode: "F2",
  },
  {
    legend: "F3",
    keycode: 60,
    id: 4,
    browserCode: "F3",
  },
  {
    legend: "F4",
    keycode: 61,
    id: 5,
    browserCode: "F4",
  },
  {
    legend: "F5",
    keycode: 62,
    id: 6,
    browserCode: "F5",
  },
  {
    legend: "F6",
    keycode: 63,
    id: 7,
    browserCode: "F6",
  },
  {
    legend: "F7",
    keycode: 64,
    id: 8,
    browserCode: "F7",
  },
  {
    legend: "F8",
    keycode: 65,
    id: 9,
    browserCode: "F8",
  },
  {
    legend: "F9",
    keycode: 66,
    id: 10,
    browserCode: "F9",
  },
  {
    legend: "F10",
    keycode: 67,
    id: 11,
    browserCode: "F10",
  },
  {
    legend: "F11",
    keycode: 68,
    id: 12,
    browserCode: "F11",
  },
  {
    legend: "F12",
    keycode: 69,
    id: 13,
    browserCode: "F12",
  },
  {
    legend: "F13",
    keycode: 104,
    id: 114,
    browserCode: "F13",
  },
  {
    legend: "F14",
    keycode: 105,
    id: 115,
    browserCode: "F14",
  },
  {
    legend: "F15",
    keycode: 106,
    id: 116,
    browserCode: "F15",
  },
  {
    legend: "`~",
    keycode: 53,
    id: 14,
    browserCode: "Backquote",
  },
  {
    legend: "1",
    keycode: 30,
    id: 15,
    browserCode: "Digit1",
  },
  {
    legend: "2",
    keycode: 31,
    id: 16,
    browserCode: "Digit2",
  },
  {
    legend: "3",
    keycode: 32,
    id: 17,
    browserCode: "Digit3",
  },
  {
    legend: "4",
    keycode: 33,
    id: 18,
    browserCode: "Digit4",
  },
  {
    legend: "5",
    keycode: 34,
    id: 19,
    browserCode: "Digit5",
  },
  {
    legend: "6",
    keycode: 35,
    id: 20,
    browserCode: "Digit6",
  },
  {
    legend: "7",
    keycode: 36,
    id: 21,
    browserCode: "Digit7",
  },
  {
    legend: "8",
    keycode: 37,
    id: 22,
    browserCode: "Digit8",
  },
  {
    legend: "9",
    keycode: 38,
    id: 23,
    browserCode: "Digit9",
  },
  {
    legend: "0",
    keycode: 39,
    id: 24,
    browserCode: "Digit0",
  },
  {
    legend: "-_",
    keycode: 45,
    id: 25,
    browserCode: "Minus",
  },
  {
    legend: "=+",
    keycode: 46,
    id: 26,
    browserCode: "Equal",
  },
  {
    legend: "Back Space",
    keycode: 42,
    id: 27,
    browserCode: "Backspace",
  },
  {
    legend: "TAB",
    keycode: 43,
    id: 28,
    browserCode: "Tab",
  },
  {
    legend: "Q",
    keycode: 20,
    id: 29,
    browserCode: "KeyQ",
  },
  {
    legend: "W",
    keycode: 26,
    id: 30,
    browserCode: "KeyW",
  },
  {
    legend: "E",
    keycode: 8,
    id: 31,
    browserCode: "KeyE",
  },
  {
    legend: "R",
    keycode: 21,
    id: 32,
    browserCode: "KeyR",
  },
  {
    legend: "T",
    keycode: 23,
    id: 33,
    browserCode: "KeyT",
  },
  {
    legend: "Y",
    keycode: 28,
    id: 34,
    browserCode: "KeyY",
  },
  {
    legend: "U",
    keycode: 24,
    id: 35,
    browserCode: "KeyU",
  },
  {
    legend: "I",
    keycode: 12,
    id: 36,
    browserCode: "KeyI",
  },
  {
    legend: "O",
    keycode: 18,
    id: 37,
    browserCode: "KeyO",
  },
  {
    legend: "P",
    keycode: 19,
    id: 38,
    browserCode: "KeyP",
  },
  {
    legend: "[{",
    keycode: 47,
    id: 39,
    browserCode: "BracketLeft",
  },
  {
    legend: "}]",
    keycode: 48,
    id: 40,
    browserCode: "BracketRight",
  },
  {
    legend: "\\|",
    keycode: 49,
    id: 41,
    browserCode: "Backslash",
  },
  {
    legend: "Caps Lock",
    keycode: 57,
    id: 42,
    browserCode: "CapsLock",
  },
  {
    legend: "A",
    keycode: 4,
    id: 43,
    browserCode: "KeyA",
  },
  {
    legend: "S",
    keycode: 22,
    id: 44,
    browserCode: "KeyS",
  },
  {
    legend: "D",
    keycode: 7,
    id: 45,
    browserCode: "KeyD",
  },
  {
    legend: "F",
    keycode: 9,
    id: 46,
    browserCode: "KeyF",
  },
  {
    legend: "G",
    keycode: 10,
    id: 47,
    browserCode: "KeyG",
  },
  {
    legend: "H",
    keycode: 11,
    id: 48,
    browserCode: "KeyH",
  },
  {
    legend: "J",
    keycode: 13,
    id: 49,
    browserCode: "KeyJ",
  },
  {
    legend: "K",
    keycode: 14,
    id: 50,
    browserCode: "KeyK",
  },
  {
    legend: "L",
    keycode: 15,
    id: 51,
    browserCode: "KeyL",
  },
  {
    legend: ";:",
    keycode: 51,
    id: 52,
    browserCode: "Semicolon",
  },
  {
    legend: "'\"",
    keycode: 52,
    id: 53,
    browserCode: "Quote",
  },
  {
    legend: "Enter",
    keycode: 40,
    id: 54,
    browserCode: "Enter",
  },
  {
    legend: "Left Shift",
    keycode: 131072,
    id: 55,
    browserCode: "ShiftLeft",
    xsKeycode: 225,
  },
  {
    legend: "Z",
    keycode: 29,
    id: 56,
    browserCode: "KeyZ",
  },
  {
    legend: "X",
    keycode: 27,
    id: 57,
    browserCode: "KeyX",
  },
  {
    legend: "C",
    keycode: 6,
    id: 58,
    browserCode: "KeyC",
  },
  {
    legend: "V",
    keycode: 25,
    id: 59,
    browserCode: "KeyV",
  },
  {
    legend: "B",
    keycode: 5,
    id: 60,
    browserCode: "KeyB",
  },
  {
    legend: "N",
    keycode: 17,
    id: 61,
    browserCode: "KeyN",
  },
  {
    legend: "M",
    keycode: 16,
    id: 62,
    browserCode: "KeyM",
  },
  {
    legend: ",<",
    keycode: 54,
    id: 63,
    browserCode: "Comma",
  },
  {
    legend: ".>",
    keycode: 55,
    id: 64,
    browserCode: "Period",
  },
  {
    legend: "/?",
    keycode: 56,
    id: 65,
    browserCode: "Slash",
  },
  {
    legend: "Right Shift",
    keycode: 2097152,
    id: 66,
    browserCode: "ShiftRight",
    xsKeycode: 229,
  },
  {
    legend: "Left Ctrl",
    keycode: 65536,
    id: 67,
    browserCode: "ControlLeft",
    xsKeycode: 224,
  },
  {
    legend: "Win Lock",
    keycode: 524288,
    id: 68,
    browserCode: "MetaLeft",
    xsKeycode: 227,
  },
  {
    legend: "Left Alt",
    keycode: 262144,
    id: 69,
    browserCode: "AltLeft",
    xsKeycode: 226,
  },
  {
    legend: "Space",
    keycode: 44,
    id: 70,
    browserCode: "Space",
  },
  {
    legend: "Right Alt",
    keycode: 4194304,
    id: 71,
    browserCode: "AltRight",
    xsKeycode: 230,
  },
  {
    legend: "FN",
    keycode: 218103808,
    id: 72,
    browserCode: "",
    xsKeycode: 61697,
  },
  {
    legend: "Right Ctrl",
    keycode: 1048576,
    id: 73,
    browserCode: "ControlRight",
    xsKeycode: 228,
  },
  {
    legend: "Up",
    keycode: 82,
    id: 74,
    browserCode: "ArrowUp",
  },
  {
    legend: "Down",
    keycode: 81,
    id: 75,
    browserCode: "ArrowDown",
  },
  {
    legend: "Left",
    keycode: 80,
    id: 76,
    browserCode: "ArrowLeft",
  },
  {
    legend: "Right",
    keycode: 79,
    id: 77,
    browserCode: "ArrowRight",
  },
  {
    legend: "Num Lock",
    keycode: 83,
    id: 78,
    browserCode: "NumLock",
  },
  {
    legend: "/",
    keycode: 84,
    id: 79,
    browserCode: "NumpadDivide",
  },
  {
    legend: "*",
    keycode: 85,
    id: 80,
    browserCode: "NumpadMultiply",
  },
  {
    legend: "-",
    keycode: 86,
    id: 81,
    browserCode: "NumpadSubtract",
  },
  {
    legend: "+",
    keycode: 87,
    id: 82,
    browserCode: "NumpadAdd",
  },
  {
    legend: "Num Enter",
    keycode: 88,
    id: 83,
    browserCode: "NumpadEnter",
  },
  {
    legend: "Num Decimal",
    keycode: 99,
    id: 84,
    browserCode: "NumpadDecimal",
  },
  {
    legend: "Num 0",
    keycode: 98,
    id: 85,
    browserCode: "Numpad0",
  },
  {
    legend: "Num 1",
    keycode: 89,
    id: 86,
    browserCode: "Numpad1",
  },
  {
    legend: "Num 2",
    keycode: 90,
    id: 87,
    browserCode: "Numpad2",
  },
  {
    legend: "Num 3",
    keycode: 91,
    id: 88,
    browserCode: "Numpad3",
  },
  {
    legend: "Num 4",
    keycode: 92,
    id: 89,
    browserCode: "Numpad4",
  },
  {
    legend: "Num 5",
    keycode: 93,
    id: 90,
    browserCode: "Numpad5",
  },
  {
    legend: "Num 6",
    keycode: 94,
    id: 91,
    browserCode: "Numpad6",
  },
  {
    legend: "Num 7",
    keycode: 95,
    id: 92,
    browserCode: "Numpad7",
  },
  {
    legend: "Num 8",
    keycode: 96,
    id: 93,
    browserCode: "Numpad8",
  },
  {
    legend: "Num 9",
    keycode: 97,
    id: 94,
    browserCode: "Numpad9",
  },
  {
    legend: "Print Screen",
    keycode: 70,
    id: 95,
    browserCode: "PrintScreen",
  },
  {
    legend: "Scroll Lock",
    keycode: 71,
    id: 96,
    browserCode: "ScrollLock",
  },
  {
    legend: "Pause",
    keycode: 72,
    id: 97,
    browserCode: "Pause",
  },
  {
    legend: "Insert",
    keycode: 73,
    id: 98,
    browserCode: "Insert",
  },
  {
    legend: "Delete",
    keycode: 76,
    id: 99,
    browserCode: "Delete",
  },
  {
    legend: "Home",
    keycode: 74,
    id: 100,
    browserCode: "Home",
  },
  {
    legend: "End",
    keycode: 77,
    id: 101,
    browserCode: "End",
  },
  {
    legend: "Page Up",
    keycode: 75,
    id: 102,
    browserCode: "PageUp",
  },
  {
    legend: "Page Down",
    keycode: 78,
    id: 103,
    browserCode: "PageDown",
  },
  {
    legend: "Context Menu",
    keycode: 101,
    id: 104,
    browserCode: "ContextMenu",
  },
  {
    legend: "K45",
    keycode: 100,
    id: 105,
    browserCode: "",
  },
  {
    legend: "K131",
    keycode: 139,
    id: 106,
    browserCode: "",
  },
  {
    legend: "K132",
    keycode: 138,
    id: 107,
    browserCode: "",
  },
  {
    legend: "K133",
    keycode: 136,
    id: 108,
    browserCode: "",
  },
  {
    legend: "K151",
    keycode: 144,
    id: 109,
    browserCode: "",
  },
  {
    legend: "K150",
    keycode: 145,
    id: 110,
    browserCode: "",
  },
  {
    legend: "K42",
    keycode: 50,
    id: 111,
    browserCode: "",
  },
  {
    legend: "Brightness Up",
    keycode: 33554543,
    id: 256,
    browserCode: "BrightnessUp",
  },
  {
    legend: "Brightness Down",
    keycode: 33554544,
    id: 512,
    browserCode: "BrightnessDown",
  },
  {
    legend: "NextTr",
    keycode: 33554613,
    id: 1024,
    browserCode: "MediaTrackNext",
    xsKeycode: 8373,
  },
  {
    legend: "PrevTr",
    keycode: 33554614,
    id: 1280,
    browserCode: "MediaTrackPrevious",
    xsKeycode: 8374,
  },
  {
    legend: "Stop",
    keycode: 33554615,
    id: 1536,
    browserCode: "MediaStop",
  },
  {
    legend: "Eject",
    keycode: 33554616,
    id: 1792,
    browserCode: "Eject",
  },
  {
    legend: "PlayPause",
    keycode: 33554637,
    id: 2048,
    browserCode: "MediaPlayPause",
    xsKeycode: 8397,
  },
  {
    legend: "Mute",
    keycode: 33554658,
    id: 2304,
    browserCode: "AudioVolumeMute",
    xsKeycode: 8418,
  },
  {
    legend: "VolumI",
    keycode: 33554665,
    id: 2560,
    browserCode: "AudioVolumeUp",
    xsKeycode: 8425,
  },
  {
    legend: "VolumD",
    keycode: 33554666,
    id: 2816,
    browserCode: "AudioVolumeDown",
    xsKeycode: 8426,
  },
  {
    legend: "Media",
    keycode: 33554819,
    id: 3072,
    browserCode: "LaunchMediaPlayer",
  },
  {
    legend: "Email",
    keycode: 33554826,
    id: 3328,
    browserCode: "LaunchMail",
  },
  {
    legend: "Calculator",
    keycode: 33554834,
    id: 3584,
    browserCode: "LaunchApp2",
    xsKeycode: 8594,
  },
  {
    legend: "MyComputer",
    keycode: 33554836,
    id: 3840,
    browserCode: "LaunchApp1",
  },
  {
    legend: "Search",
    keycode: 33554977,
    id: 4096,
    browserCode: "BrowserSearch",
  },
  {
    legend: "WWW",
    keycode: 33554979,
    id: 4352,
    browserCode: "BrowserHome",
  },
  {
    legend: "Back",
    keycode: 33554980,
    id: 4608,
    browserCode: "BrowserBack",
  },
  {
    legend: "Forward",
    keycode: 33554981,
    id: 4864,
    browserCode: "BrowserForward",
  },
  {
    legend: "iStop",
    keycode: 33554982,
    id: 5120,
    browserCode: "BrowserStop",
  },
  {
    legend: "Refresh",
    keycode: 33554983,
    id: 5376,
    browserCode: "BrowserRefresh",
  },
  {
    legend: "Favorites",
    keycode: 33554986,
    id: 5632,
    browserCode: "BrowserFavorites",
  },
  {
    legend: "Win+L",
    keycode: 524303,
    id: 32768,
    browserCode: "",
  },
  {
    legend: "Win+D",
    keycode: 524295,
    id: 32769,
    browserCode: "",
  },
];
export const MEDIA_KEYS_BEIYING = [
  {
    legend: "Play/Pause",
    keycode: 33554637,
    i18nKey: "playPause",
    xsKeycode: 8397,
  },
  {
    legend: "System Multimedia",
    keycode: 33554819,
    i18nKey: "systemMultimedia",
  },
  {
    legend: "Mute",
    keycode: 33554658,
    i18nKey: "muteKey",
    xsKeycode: 8418,
  },
  {
    legend: "Pause",
    keycode: 33554615,
    i18nKey: "pauseMedia",
  },
  {
    legend: "Previous",
    keycode: 33554614,
    i18nKey: "previousSong",
    xsKeycode: 8374,
  },
  {
    legend: "Next",
    keycode: 33554613,
    i18nKey: "nextSong",
    xsKeycode: 8373,
  },
  {
    legend: "Volume Up",
    keycode: 33554665,
    i18nKey: "volumeUp",
    xsKeycode: 8425,
  },
  {
    legend: "Volume Down",
    keycode: 33554666,
    i18nKey: "volumeDown",
    xsKeycode: 8426,
  },
];

export const KEYCODE_MAP_BEIYING: DefaultKeycap[] = KEYCODE_MAP_BEIYING_RAW.map(
  (k) => {
    const m80pos = resolveM80PosForKeycap(k);
    const base = {
      ...k,
      ...(Number.isFinite(m80pos) ? { m80pos: m80pos as number } : {}),
    };
    /** 固件计算器 value ↔ 矩阵/KEYCODE_MAP 星闪码 8594（与 `KEYBOARD_LAYOUTS["98"]` 一致） */
    if (k.keycode === 33554834 && base.xsKeycode == null) {
      return { ...base, xsKeycode: 8594 };
    }
    if (k.keycode === 33554836 && base.xsKeycode == null) {
      return { ...base, xsKeycode: 8596 };
    }
    if (k.keycode === 33554979 && base.xsKeycode == null) {
      return { ...base, xsKeycode: 8739 };
    }
    return base;
  }
);

export const M80_FN_KEYCODE_MAP_BEIYING: DefaultKeycap[] =
  KEYCODE_MAP_BEIYING_RAW.map((k) => {
    const m80pos = resolveM80FnPosForKeycap(k);
    const base = {
      ...k,
      ...(Number.isFinite(m80pos) ? { m80pos: m80pos as number } : {}),
    };
    /** 固件计算器 value ↔ 矩阵/KEYCODE_MAP 星闪码 8594（与 `KEYBOARD_LAYOUTS["98"]` 一致） */
    if (k.keycode === 33554834 && base.xsKeycode == null) {
      return { ...base, xsKeycode: 8594 };
    }
    if (k.keycode === 33554836 && base.xsKeycode == null) {
      return { ...base, xsKeycode: 8596 };
    }
    if (k.keycode === 33554979 && base.xsKeycode == null) {
      return { ...base, xsKeycode: 8739 };
    }
    return base;
  });

export const MOUSE_KEYS_BEIYING = [
  {
    legend: "Mouse Left",
    keycode: 16843008,
    i18nKey: "mouseLeft",
    name: "左键",
    hex16: 0x01,
    xsKeycode: 16640,
  },
  {
    legend: "Mouse Right",
    keycode: 16908544,
    i18nKey: "mouseRight",
    name: "右键",
    hex16: 0x02,
    xsKeycode: 16896,
  },
  {
    legend: "Mouse Middle",
    keycode: 16974080,
    i18nKey: "mouseMiddle",
    name: "中键",
    hex16: 0x04,
    xsKeycode: 17152,
  },
  {
    legend: "Mouse Forward",
    keycode: 17105152,
    i18nKey: "mouseForward",
    name: "前进",
    hex16: 0x10,
  },
  {
    legend: "Mouse Back",
    keycode: 17039616,
    i18nKey: "mouseBack",
    name: "后退",
    hex16: 0x08,
  },
];

/**
 * M71 键槽列表：矩阵为 `KEYBOARD_LAYOUTS.m71`；pos 以 M71 实机 `getKeyboardKeys` 初始化表为准，再回落 M80 表。
 */
export const M71_KEYS_BEIYING: M80KeySlot[] = (
  (KEYBOARD_LAYOUTS.m71 as number[][]) || []
).flatMap((rowData: number[], row: number) =>
  rowData
    .map((keycode: number, col: number) => {
      if (!keycode) return null;
      const pos =
        M71_FACTORY_POS_BY_KEYCODE[keycode] ??
        M80_DEFAULT_POS_BY_KEYCODE[keycode];
      if (!Number.isFinite(pos)) return null;
      return { keycode, pos, row, col };
    })
    .filter((item): item is M80KeySlot => Boolean(item))
);

/**
 * T87 键槽列表：矩阵为 `KEYBOARD_LAYOUTS.t87`；pos 以 T87 实机 `getKeyboardKeys` 初始化表为准。
 */
export const T87_KEYS_BEIYING: M80KeySlot[] = (
  (KEYBOARD_LAYOUTS.t87 as number[][]) || []
).flatMap((rowData: number[], row: number) =>
  rowData
    .map((keycode: number, col: number) => {
      if (!keycode) return null;
      const pos = T87_FACTORY_POS_BY_KEYCODE[keycode];
      if (!Number.isFinite(pos)) return null;
      return { keycode, pos, row, col };
    })
    .filter((item): item is M80KeySlot => Boolean(item))
);

/**
 * X75V2 键槽列表：矩阵为 `KEYBOARD_LAYOUTS.x75v2`；pos 以 X75V2 实机 getKeyboardKeys 初始化表为准。
 */
export const X75V2_KEYS_BEIYING: M80KeySlot[] = (
  (KEYBOARD_LAYOUTS.x75v2 as number[][]) || []
).flatMap((rowData: number[], row: number) =>
  rowData
    .map((keycode: number, col: number) => {
      if (!keycode) return null;
      const pos = X75V2_FACTORY_POS_BY_KEYCODE[keycode];
      if (!Number.isFinite(pos)) return null;
      return { keycode, pos, row, col };
    })
    .filter((item): item is M80KeySlot => Boolean(item))
);

/**
 * M75 键槽列表：矩阵为 `KEYBOARD_LAYOUTS.m75`；pos 以 M75 实机 getKeyboardKeys 初始化表为准。
 */
export const M75_KEYS_BEIYING: M80KeySlot[] = (
  (KEYBOARD_LAYOUTS.m75 as number[][]) || []
).flatMap((rowData: number[], row: number) =>
  rowData
    .map((keycode: number, col: number) => {
      if (!keycode) return null;
      const pos = M75_FACTORY_POS_BY_KEYCODE[keycode];
      if (!Number.isFinite(pos)) return null;
      return { keycode, pos, row, col };
    })
    .filter((item): item is M80KeySlot => Boolean(item))
);

/**
 * 贝盈-高级键数据转换
 * start
 */
/** =========================== SOCD回显转换 start =========================== */

/** 接口返回的result类型 */
interface Result {
  ids: number[];
  responseMode: number;
  type?: number; // 原数据中的type字段，可选
}

/** 99布局的键盘结构：二维字符串数组 */
type KeyboardLayout99 = number[][];

/** 最终返回的data对象类型 */
interface KeyboardData {
  row: number;
  col: number;
  mode: number;
  data: {
    row2: number;
    col2: number;
    delay: number;
    kcs: string[];
    socdMode: number;
  };
}

// ========== 工具函数（单一职责，便于复用/测试） ==========
/**
 * 根据ID查找对应的keycode（优先xsKeycode，兜底空字符串）
 * @param id 要匹配的ID
 * @param keycodeMap 键码映射表
 */
const findKeycodeById = (id: number, keycodeMap: DefaultKeycap[]) => {
  const targetItem = keycodeMap.find((item) => item.id === id);
  return targetItem?.xsKeycode || targetItem?.keycode;
};

/** 物理灯槽 pos 映射：须按出厂/default 键值，改键后的 `value` 会错位导致实体灯不亮 */
const resolveRawKeycodeForPhysicalSlot = (item: any): unknown => {
  if (!item) return undefined;
  const direct =
    item.defaultValue ??
    item.keycode ??
    item.code ??
    item.keyValue ??
    item.value ??
    item.rawKeycode;
  if (direct != null) return direct;
  // 部分固件键表不带 defaultValue/keycode，仅给 id；用 id 反查可避免少量键位(如 Z / .> / R-Ctrl)丢失 pos 映射
  const id = Number(item?.id);
  if (Number.isFinite(id)) {
    return findKeycodeById(id, KEYCODE_MAP_BEIYING);
  }
  return undefined;
};

/**
 * 贝盈 SDK 键码归一到布局查找空间（与灯光页 KEYBOARD_LAYOUTS 选键逻辑一致）。
 */
const normalizeBeiyingLayoutKeycode = (rawKeycode: unknown): number => {
  let candidate: number | undefined;
  if (typeof rawKeycode === "number" && Number.isFinite(rawKeycode)) {
    candidate = rawKeycode;
  } else if (typeof rawKeycode === "string") {
    const raw = rawKeycode.trim();
    if (/^0x[0-9a-f]+$/i.test(raw)) {
      const parsedHex = Number.parseInt(raw, 16);
      if (Number.isFinite(parsedHex)) candidate = parsedHex;
    } else {
      const parsedDec = Number(raw);
      if (Number.isFinite(parsedDec)) candidate = parsedDec;
    }
  }
  if (candidate === undefined) {
    return 0;
  }
  const fromKeyboard = KEYCODE_MAP_BEIYING.find(
    (item) => item.keycode === candidate || item.xsKeycode === candidate
  );
  if (fromKeyboard) {
    return fromKeyboard.xsKeycode || fromKeyboard.keycode;
  }
  const fromMouse = MOUSE_KEYS_BEIYING.find(
    (item) => item.keycode === candidate || item.xsKeycode === candidate
  );
  if (fromMouse) {
    return fromMouse.xsKeycode || fromMouse.keycode;
  }
  const fromMedia = MEDIA_KEYS_BEIYING.find(
    (item) => item.keycode === candidate || item.xsKeycode === candidate
  );
  if (fromMedia) {
    return fromMedia.xsKeycode || fromMedia.keycode;
  }
  return candidate;
};

/**
 * 916 机甲：getKeyboardKeys 分位 value → `KEYBOARD_LAYOUTS` 格内 HID。
 * 自定义灯 pos→hid 常为分位值，矩阵键为 HID，须在 `buildHidKeycodeToPosMapFromKeyboardKeys` 与 `getLightingCustom` 双轨对齐。
 */
export const BEIYING916_CB_VALUE_TO_LAYOUT_HID: Readonly<
  Record<number, number>
> = {
  65536: 224,
  131072: 225,
  262144: 226,
  524288: 227,
  1048576: 228,
  2097152: 229,
  4194304: 230,
  218103808: 61697,
};

/** 8815：布局矩阵里 Modifier/Fn 的 HID → getKeyboardKeys 的 `value`（与 RAW 一致） */
export const BEIYING8815_LAYOUT_HID_TO_FIRMWARE_VALUE: Readonly<
  Record<number, number>
> = (() => {
  const o: Record<number, number> = {};
  for (const [fw, hid] of Object.entries(BEIYING916_CB_VALUE_TO_LAYOUT_HID)) {
    o[Number(hid)] = Number(fw);
  }
  return o;
})();

type BeiyingKeyCapLike = {
  keycode: number;
  xsKeycode?: number;
};

function findBeiyingKeyCap(layoutCode: number): BeiyingKeyCapLike | undefined {
  return (
    KEYCODE_MAP_BEIYING.find(
      (k) => k.keycode === layoutCode || k.xsKeycode === layoutCode
    ) ??
    MEDIA_KEYS_BEIYING.find(
      (k: { keycode?: number; xsKeycode?: number }) =>
        k.keycode === layoutCode || k.xsKeycode === layoutCode
    ) ??
    MOUSE_KEYS_BEIYING.find(
      (k) => k.keycode === layoutCode || k.xsKeycode === layoutCode
    )
  );
}

export { findBeiyingKeyCap };

/** 改键 UI 展示用 keycode（与 SDK getKeyCode 回显 xsKeycode、ICON_CONFIG 一致） */
export function resolveBeiyingRemapUiKeycode(keyCode: number): number {
  const cap = findBeiyingKeyCap(keyCode);
  if (cap?.xsKeycode != null) return cap.xsKeycode;
  return keyCode;
}

/** 统一为布局矩阵 HID，便于工厂默认与设备/快照当前值对比（改键读盘与社区预览共用） */
export function normalizeBeiyingComparableKeycode(code: number): number {
  if (!code) return 0;

  const cap = findBeiyingKeyCap(code);
  if (cap?.xsKeycode != null) return cap.xsKeycode;
  if (cap?.keycode != null) {
    const hid = BEIYING916_CB_VALUE_TO_LAYOUT_HID[cap.keycode];
    return hid ?? cap.keycode;
  }

  const fromFirmware = BEIYING916_CB_VALUE_TO_LAYOUT_HID[code];
  if (fromFirmware != null) return fromFirmware;

  return code;
}

export function beiyingKeycodesEqual(a: number, b: number): boolean {
  return (
    normalizeBeiyingComparableKeycode(a) ===
    normalizeBeiyingComparableKeycode(b)
  );
}

function findBeiyingListPrimaryKeycode(layoutCode: number): number | undefined {
  const hit =
    KEYCODE_MAP_BEIYING.find(
      (k) => k.keycode === layoutCode || k.xsKeycode === layoutCode
    ) ||
    MEDIA_KEYS_BEIYING.find(
      (k: { keycode?: number; xsKeycode?: number }) =>
        k.keycode === layoutCode || k.xsKeycode === layoutCode
    ) ||
    MOUSE_KEYS_BEIYING.find(
      (k) => k.keycode === layoutCode || k.xsKeycode === layoutCode
    );
  if (!hit) return undefined;
  return Number((hit as { keycode: number }).keycode);
}

/**
 * 8815：KEYBOARD_LAYOUTS 格码 → 与 `X98_1K_DEFAULT_POS_BY_KEYCODE` / RAW `value` 一致的主键。
 * Modifier 格为 HID（如 225）时映射为分位（131072）；计算器布局码 8594 经 KEYCODE_MAP_BEIYING.xsKeycode 落到 33554834。
 */
export function beiying8815LayoutCodeToFirmwareTableKey(
  layoutCode: number
): number {
  if (!layoutCode) return 0;
  const fromHid = BEIYING8815_LAYOUT_HID_TO_FIRMWARE_VALUE[layoutCode];
  if (fromHid !== undefined) return fromHid;
  const fromLists = findBeiyingListPrimaryKeycode(layoutCode);
  if (fromLists !== undefined) return fromLists;
  return layoutCode;
}

/**
 * 从机甲 SDK `getKeyboardKeys` 返回的键位列表构建：布局 HID keycode（如 Esc=41）→ 设备 **pos**（自定义灯 `setKeyboardCustomLightColor` 使用）。
 */
export function buildHidKeycodeToPosMapFromKeyboardKeys(
  items: any[]
): Map<number, number> {
  const map = new Map<number, number>();
  for (const item of items ?? []) {
    if (item == null || typeof item.pos !== "number") continue;
    const raw = resolveRawKeycodeForPhysicalSlot(item);
    const normalized = normalizeBeiyingLayoutKeycode(raw);
    const found = KEYCODE_MAP_BEIYING.find(
      (k) => k.keycode === normalized || k.xsKeycode === normalized
    );
    const layoutHid = found?.keycode ?? normalized;
    if (typeof layoutHid === "number" && Number.isFinite(layoutHid)) {
      map.set(layoutHid, item.pos);
      const uiHid = BEIYING916_CB_VALUE_TO_LAYOUT_HID[layoutHid];
      if (
        typeof uiHid === "number" &&
        Number.isFinite(uiHid) &&
        uiHid !== layoutHid
      ) {
        map.set(uiHid, item.pos);
      }
      if (layoutHid === 61697 || layoutHid === 218103808) {
        map.set(61697, item.pos);
        map.set(218103808, item.pos);
      }
    }
  }
  return map;
}

/**
 * 根据keycode查找在键盘布局中的行列（兜底返回[0,0]）
 * @param keycode 要匹配的键码
 * @param layout 99布局的键盘二维数组
 */
const findRowColByKeycode = (
  keycode: number,
  layout: KeyboardLayout99
): { row: number; col: number } => {
  for (let row = 0; row < layout.length; row++) {
    const col = layout[row].indexOf(keycode);
    if (col !== -1) return { row, col };
  }
  return { row: 0, col: 0 }; // 未找到时兜底
};

// ========== 主函数（核心逻辑） ==========
/**
 * 生成格式化的键盘配置数据
 * @param result 接口返回的原始数据（含ids/responseMode）
 * @param KEYCODE_MAP_BEIYING 键码映射表
 * @param KEYBOARD_LAYOUTS_99 99布局的键盘二维数组
 * @returns 标准化的data对象
 */
export const getKeyboardSOCD = (
  result: Result,
  KEYCODE_MAP_BEIYING: DefaultKeycap[],
  KEYBOARD_LAYOUTS_99: KeyboardLayout99
): KeyboardData => {
  // 解构处理ids，避免长度不足导致报错（兜底0）
  const [firstId = 0, secondId = 0] = result.ids;

  // 1. 获取两个按键的keycode
  const firstKeycode: any = findKeycodeById(firstId, KEYCODE_MAP_BEIYING);
  const secondKeycode: any = findKeycodeById(secondId, KEYCODE_MAP_BEIYING);

  // 2. 获取两个按键的行列位置
  const { row: firstRow, col: firstCol } = findRowColByKeycode(
    firstKeycode,
    KEYBOARD_LAYOUTS_99
  );
  const { row: secondRow, col: secondCol } = findRowColByKeycode(
    secondKeycode,
    KEYBOARD_LAYOUTS_99
  );

  // 3. 简化socdMode映射（对象替代嵌套三元，易扩展）
  const socdModeMap: any = { 1: 0, 3: 1, 4: 2 };
  const socdMode: any = socdModeMap[result.responseMode] ?? 3; // 兜底3

  // 4. 构造最终返回对象
  return {
    row: firstRow,
    col: firstCol,
    mode: 6,
    data: {
      row2: secondRow,
      col2: secondCol,
      delay: 0,
      kcs: [firstKeycode, secondKeycode],
      socdMode,
    },
  };
};

/** =========================== SOCD回显转换 end =========================== */

/** =========================== MT回显转换 start =========================== */
/** Result 数据结构（匹配传入格式） */
interface MTResult {
  id: number;
  clickKeycode: number;
  holdKeycode: number;
  delay: number;
  [key: string]: any; // 兼容其他扩展字段
}
/** 最终返回的 data 结构类型 */
interface MTKeyboardData {
  row: string | number;
  col: string | number;
  mode: 3;
  data: {
    kcs: [number, number];
    time: number;
  };
}
/**
 * 生成模式 3 的键盘配置数据（对齐原代码逻辑）
 * @param result 原始数据（含 id/clickKeycode/holdKeycode/delay）
 * @param KEYCODE_MAP_BEIYING 键码映射表
 * @param KEYBOARD_LAYOUTS_99 99 布局的键盘二维数组（对应原 KEYBOARD_LAYOUTS["99"]）
 * @returns 标准化的 data 对象
 */
export const getKeyboardMT = (
  result: MTResult,
  KEYCODE_MAP_BEIYING: DefaultKeycap[],
  KEYBOARD_LAYOUTS_99: KeyboardLayout99
): MTKeyboardData => {
  // 字段兜底：避免 result 缺失关键字段导致报错
  const targetId = result.id ?? 0;
  const clickKeycode = result.clickKeycode ?? 0;
  const holdKeycode = result.holdKeycode ?? 0;
  const delay = result.delay ?? 0;

  // 1. 根据 result.id 查找对应的 keycode
  const idKey: any = findKeycodeById(targetId, KEYCODE_MAP_BEIYING);

  // 2. 查找 keycode 对应的行列（优化遍历逻辑）
  const { row, col } = findRowColByKeycode(idKey, KEYBOARD_LAYOUTS_99);

  // 3. 构造并返回最终数据（严格对齐原代码结构）
  return {
    row,
    col,
    mode: 3,
    data: {
      kcs: [clickKeycode, holdKeycode], // 原逻辑：直接取 result 中的原始值，非映射后 keycode
      time: delay,
    },
  };
};

/** =========================== MT回显转换 end =========================== */

/** =========================== TGL回显转换 start =========================== */
interface TGLResult {
  delay: number;
  id: number;
  keycode: number;
  type: number;
  [key: string]: any; // 兼容扩展字段
}
/**
 * 生成 TGL 模式的键盘配置数据
 * @param result TGL模式原始数据
 * @param KEYCODE_MAP_BEIYING 键码映射表
 * @param KEYBOARD_LAYOUTS_99 99布局的键盘二维数组
 * @returns TGL模式的 data 对象
 */
export const getKeyboardTGL = (
  result: TGLResult,
  KEYCODE_MAP_BEIYING: DefaultKeycap[],
  KEYBOARD_LAYOUTS_99: KeyboardLayout99
) => {
  // 字段兜底，避免缺失字段报错
  const targetId = result.id ?? 0;

  // 查找 id 对应的 keycode
  const idKey: any = findKeycodeById(targetId, KEYCODE_MAP_BEIYING);

  // 查找行列
  const { row, col } = findRowColByKeycode(idKey, KEYBOARD_LAYOUTS_99);

  // 返回最终数据
  return {
    row,
    col,
    mode: 4,
    data: {
      kcs: result.keycode,
      time: 0,
    },
  };
};
/** =========================== TGL回显转换 end =========================== */

/** =========================== END回显转换 start =========================== */
interface ENDResult {
  id: number;
  keycode: number;
  type: number;
  [key: string]: any; // 兼容扩展字段
}
/**
 * 生成 TGL 模式的键盘配置数据
 * @param result TGL模式原始数据
 * @param KEYCODE_MAP_BEIYING 键码映射表
 * @param KEYBOARD_LAYOUTS_99 99布局的键盘二维数组
 * @returns TGL模式的 data 对象
 */
export const getKeyboardEND = (
  result: ENDResult,
  KEYCODE_MAP_BEIYING: DefaultKeycap[],
  KEYBOARD_LAYOUTS_99: KeyboardLayout99
) => {
  // 字段兜底，避免缺失字段报错
  const targetId = result.id ?? 0;

  // 查找 id 对应的 keycode
  const idKey: any = findKeycodeById(targetId, KEYCODE_MAP_BEIYING);

  // 查找行列
  const { row, col } = findRowColByKeycode(idKey, KEYBOARD_LAYOUTS_99);

  // 返回最终数据
  return {
    row,
    col,
    mode: 5,
    data: {
      kcs: [result.keycode, result.keycode],
      delay: 1,
    },
  };
};
/** =========================== END回显转换 end =========================== */

/**
 * 贝盈-高级键数据转换
 * start
 */

const DEFAULT_BEIYING_READY_TIMEOUT_MS = 60_000;
const DEFAULT_BEIYING_READY_INTERVAL_MS = 200;
/** 单次 getKeymaps 若永不 resolve，外层 deadline 无法生效，必须对每次 await 限時 */
const BEIYING_GET_KEYMAPS_CALL_TIMEOUT_MS = 5_000;

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tid = setTimeout(() => reject(new Error(`${label}（${ms}ms）`)), ms);
    promise.then(
      (v) => {
        clearTimeout(tid);
        resolve(v);
      },
      (e) => {
        clearTimeout(tid);
        reject(e);
      }
    );
  });
}

/** 仅用于布局拉取：等 remapping.getKeymaps 可用 */
async function awaitBeiyingRemappingReadyForLayout(
  beiyingKeyboard: { instance?: any } | null | undefined,
  options?: { timeoutMs?: number; intervalMs?: number }
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_BEIYING_READY_TIMEOUT_MS;
  const intervalMs = options?.intervalMs ?? DEFAULT_BEIYING_READY_INTERVAL_MS;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const inst = beiyingKeyboard?.instance;
    if (typeof inst?.getKeyboardKeys === "function") {
      return;
    }
    const getKeymaps = inst?.remapping?.getKeymaps;
    if (typeof getKeymaps === "function") {
      try {
        const data = await withTimeout(
          getKeymaps.call(inst.remapping, {
            layer: LayerType.Normal,
            system: SystemType.Windows,
            ids: [1],
          }),
          BEIYING_GET_KEYMAPS_CALL_TIMEOUT_MS,
          "beiying remapping probe getKeymaps"
        );
        if (Array.isArray(data)) {
          return;
        }
      } catch {
        // 设备尚未完全就绪，继续轮询
      }
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }

  throw new Error(
    "[getInitializeKeyboard] 超时：贝盈 remapping 未就绪或 getKeymaps 不可用，请确认已连接设备"
  );
}

/** ES87 F13 槽出厂态：固件读键常回 Delete(76) 或空，虚拟键盘统一显示 F13(104) */
export function normalizeEs87F13SlotDisplayKeycode(
  deviceValue: number | null | undefined
): number {
  if (
    deviceValue == null ||
    !Number.isFinite(Number(deviceValue)) ||
    Number(deviceValue) === 0 ||
    Number(deviceValue) === 76
  ) {
    return 104;
  }
  return Number(deviceValue);
}

export function resolveEs87LayoutCellDisplayValue(
  layoutHid: number,
  deviceValue: number | null | undefined
): number {
  if (layoutHid === 104) {
    return normalizeEs87F13SlotDisplayKeycode(deviceValue);
  }
  return deviceValue ?? layoutHid;
}

/** EXX Alpha 87（es87）F13 槽在布局矩阵中的 HID */
export const ES87_F13_LAYOUT_KEYCODE = 104;

/** 改键目标是否为 ES87 的 F13 槽（该键位不可改） */
export function isEs87F13RemapSlot(
  keyboardType: string,
  row: number,
  col: number
): boolean {
  if (keyboardType !== "es87") return false;
  const layout = KEYBOARD_LAYOUTS.es87;
  const slotHid = layout?.[row]?.[col];
  return slotHid === ES87_F13_LAYOUT_KEYCODE;
}

function resolveBeiyingLayoutType(
  equipmentData: any
): keyof typeof KEYBOARD_LAYOUTS {
  const keyboardType = String(equipmentData?.keyboardType ?? "");
  const solutionRovider = String(equipmentData?.solutionRovider ?? "");
  if (
    solutionRovider === "beiying" &&
    isBeiyingDevice8815X98Keyboard(equipmentData)
  ) {
    return "X98_1K";
  }
  return keyboardType as keyof typeof KEYBOARD_LAYOUTS;
}

/**
 * 分位矩阵里的一项 → 固件表用的主键（与 RAW `value` / pos 表一致；Modifier 为 HID 时先映射为分位）
 */
function beiyingHidKeycodeForLayoutKey(layoutCode: number): number {
  return beiying8815LayoutCodeToFirmwareTableKey(layoutCode);
}

/** 由一次 getKeyboardKeys 的 defaultValue/原始项建立 HID → 固件 pos，作为静态表的补集（如 8594 等） */
function build8815HidToPosFromKeyTable(keys: any[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const item of keys) {
    const pos = Number(item?.pos);
    if (!Number.isFinite(pos)) continue;
    const raw =
      item?.defaultValue ?? item?.keycode ?? item?.value ?? item?.rawKeycode;
    const n0 = Number(raw);
    if (!Number.isFinite(n0) || n0 === 0) continue;
    const hit = KEYCODE_MAP_BEIYING.find(
      (k) => k.keycode === n0 || k.xsKeycode === n0
    );
    const n = hit ? hit.keycode : n0;
    if (Number.isFinite(n) && n !== 0 && !map.has(n)) {
      map.set(n, pos);
    }
  }
  return map;
}

function resolveX98815FirmwarePos(
  hid: number,
  hidToPos: Map<number, number>
): number | undefined {
  const table = X98_1K_DEFAULT_POS_BY_KEYCODE as Record<number, number>;
  if (Object.prototype.hasOwnProperty.call(table, hid)) {
    return table[hid];
  }
  const p = hidToPos.get(hid);
  if (Number.isFinite(p as number)) return p as number;
  return undefined;
}

function mergeHidToPos(
  base: Map<number, number>,
  over: Map<number, number>
): Map<number, number> {
  const m = new Map(base);
  for (const [k, v] of over) m.set(k, v);
  return m;
}

/**
 * 以 8815 layer0 工厂表建的 HID→pos，与 m80 的静态补集等效，避免仅设备上能解析 8594 等扩键。
 * 在 X98(8815) 路径与 `build8815HidToPosFromKeyTable(getKeyboardKeys 结果)` 合并，后者可覆盖/补充。
 */
const X98_8815_FACTORY_HID_TO_POS: Map<number, number> = (() => {
  return build8815HidToPosFromKeyTable(
    BEIYING_8815_LAYER0_KEYBOARD_KEYS as any[]
  );
})();

/**
 * 矩阵格布局码（如 225、8594、131072/41）→ 设备 pos；用于改键/展示与 m80-1k `M80_KEY_SLOT` 同源思路。
 * `devKeys` 可选：有则与工厂表合并，便于解析设备独有键位。
 */
export function resolveX98_1KPosForLayoutKey(
  layoutCode: number,
  devHidToPos?: Map<number, number>
): number | undefined {
  if (!layoutCode) return undefined;
  const hid = beiyingHidKeycodeForLayoutKey(layoutCode);
  const merged = devHidToPos
    ? mergeHidToPos(X98_8815_FACTORY_HID_TO_POS, devHidToPos)
    : X98_8815_FACTORY_HID_TO_POS;
  return resolveX98815FirmwarePos(hid, merged);
}

/**
 * 与 m80-1k `M80_KEYS_BEIYING` 同构：布局矩阵每格 + 键槽 `pos` + 出厂 `firmwareValue`（RAW 的 `value`）。
 */
export const X98_1K_KEYS_BEIYING: X98_1KKeySlot[] = (() => {
  const layout = (KEYBOARD_LAYOUTS.X98_1K as number[][]) || [];
  const byPos: Map<number, (typeof BEIYING_8815_LAYER0_KEYBOARD_KEYS)[0]> =
    new Map();
  for (const it of BEIYING_8815_LAYER0_KEYBOARD_KEYS) {
    byPos.set(Number((it as any).pos), it);
  }
  return layout
    .flatMap((rowData, row) =>
      rowData.map((layoutCode, col) => {
        if (!layoutCode) return null;
        const pos = resolveX98_1KPosForLayoutKey(layoutCode);
        if (pos == null) return null;
        const r = byPos.get(pos);
        return {
          keycode: layoutCode,
          pos,
          row,
          col,
          firmwareValue: r ? Number((r as any).value) : 0,
        } satisfies X98_1KKeySlot;
      })
    )
    .filter((x): x is X98_1KKeySlot => x != null);
})();

/**
 * 供主应用 8815 改键/读键：先工厂基准再叠当前层 `getKeyboardKeys`。
 */
export function getX98_1kMergedHidToPosMapFromDeviceKeys(
  keys: any[] | null | undefined
): Map<number, number> {
  return mergeHidToPos(
    X98_8815_FACTORY_HID_TO_POS,
    build8815HidToPosFromKeyTable(Array.isArray(keys) ? keys : [])
  );
}

/**
 * 8815 `getKeyboardKeys` 里宏槽常为设备列表 **1-based**（如 1=M0、2=M1），
 * UI / KEYCODE_MACRO_MAP.index 为 **0-based**。用于读键回显与 pos→宏映射。
 */
export function normalize8815DeviceMacroIndexToUi(
  raw: unknown
): number | undefined {
  if (raw === undefined || raw === null) return undefined;
  const mi = Number(raw);
  if (!Number.isFinite(mi) || mi < 0) return undefined;
  if (mi === 0) return 0;
  if (mi >= 1 && mi <= 64) return mi - 1;
  return mi;
}

/** 与 main-app `beiyingSdkKeyLibrary` 中常量一致 */
const BEIYING_8815_MACRO_BINDING_STORAGE_KEY =
  "BEIYING_8815_MACRO_BINDING_BY_LAYER";

function read8815MacroBindingStore(): Record<string, Record<string, number>> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(BEIYING_8815_MACRO_BINDING_STORAGE_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw) as unknown;
    return p && typeof p === "object"
      ? (p as Record<string, Record<string, number>>)
      : {};
  } catch {
    return {};
  }
}

function get8815MacroIndexByLayerPos(
  layer: number,
  pos: number
): number | undefined {
  const store = read8815MacroBindingStore();
  const v = Number(store[String(layer)]?.[String(pos)]);
  return Number.isFinite(v) ? v : undefined;
}

function build8815PosToMacroIndexMap(keys: any[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const k of keys || []) {
    const pos = Number(k?.pos);
    if (!Number.isFinite(pos)) continue;
    if (
      String(k?.type ?? "")
        .trim()
        .toUpperCase() !== "M"
    )
      continue;
    const raw = k?.macroIndex ?? (k as any)?.macroId ?? (k as any)?.id;
    const ui = normalize8815DeviceMacroIndexToUi(raw);
    if (ui === undefined) continue;
    map.set(pos, ui);
  }
  return map;
}

function getMacroUiKeycodeByIndex8815(indexLike: unknown): number | undefined {
  const index = Number(indexLike);
  if (!Number.isFinite(index) || index < 0) return undefined;
  for (const [k, v] of Object.entries(KEYCODE_MACRO_MAP)) {
    if (!/^\d+$/.test(String(k))) continue;
    if (Number((v as { index?: number }).index) === index) {
      return Number(k);
    }
  }
  return undefined;
}

function mapDeviceValueToMacroUi8815(rk: number): number | undefined {
  if (!Number.isFinite(rk)) return undefined;
  for (const [k, v] of Object.entries(KEYCODE_MACRO_MAP)) {
    if (!/^\d+$/.test(String(k))) continue;
    const ui = Number(k);
    if (ui === rk) return ui;
    const hexStr = String((v as { hex16?: string }).hex16 ?? "").trim();
    if (/^0x[0-9a-f]+$/i.test(hexStr)) {
      const n = Number.parseInt(hexStr, 16);
      if (Number.isFinite(n) && n === rk) return ui;
    }
  }
  return undefined;
}

/** 与 beiying2 `mapRawToUiKey` 同源：xsKeycode 展开 + 宏设备值 + beiyingMacroKeyData */
function map8815RawValueToUiKeycode(rk: number): number {
  let v: any = rk;
  for (const list of [
    KEYCODE_MAP_BEIYING,
    MOUSE_KEYS_BEIYING,
    MEDIA_KEYS_BEIYING,
  ] as const) {
    for (const mk of list) {
      if (mk.xsKeycode && mk.keycode === v) v = mk.xsKeycode;
    }
  }
  const macroByVal = mapDeviceValueToMacroUi8815(Number(v));
  if (Number.isFinite(macroByVal)) return macroByVal as number;
  try {
    const raw = localStorage.getItem("beiyingMacroKeyData");
    if (raw) {
      const macroList = JSON.parse(raw) as unknown;
      if (Array.isArray(macroList)) {
        for (let idx = 0; idx < macroList.length; idx++) {
          if (Number(v) === macroList[idx]) {
            for (const [keyStr, info] of Object.entries(KEYCODE_MAP)) {
              if ((info as { index?: number }).index === idx) {
                return Number(keyStr);
              }
            }
          }
        }
      }
    }
  } catch {
    /* ignore */
  }
  return Number(v);
}

/**
 * X98 初始化矩阵一格 → UI keycode（62720+ 宏、与改键 getKeyCode 一致）
 */
function resolveX98815InitializeCell(
  layoutCode: number,
  machineLayer: number,
  posToMacro: Map<number, number>,
  hidToPos: Map<number, number>,
  keyByPos: Map<number, number>
): number {
  if (layoutCode === 0) return 0;
  const hid = beiyingHidKeycodeForLayoutKey(layoutCode);
  const fPos = resolveX98815FirmwarePos(hid, hidToPos);
  if (fPos === undefined) return layoutCode;
  const fromStore = get8815MacroIndexByLayerPos(machineLayer, fPos);
  const macroIdxRaw = normalize8815DeviceMacroIndexToUi(posToMacro.get(fPos));
  const macroIdx = fromStore !== undefined ? fromStore : macroIdxRaw;
  const macroUi = getMacroUiKeycodeByIndex8815(macroIdx);
  if (Number.isFinite(macroUi)) return macroUi as number;
  const rawKeyVal = keyByPos.get(fPos) ?? keyByPos.get(fPos + 1);
  if (rawKeyVal !== undefined && Number.isFinite(rawKeyVal)) {
    return map8815RawValueToUiKeycode(Number(rawKeyVal));
  }
  return layoutCode;
}

/**
 * 贝盈-初始化键盘数据转换
 * @param system 0=Windows，1=macOS（与改键页 Win/Mac Base 对应，勿写死 Windows）
 */
export const getInitializeKeyboard = async (
  layer: any,
  beiyingKeyboard: any,
  system: number = 0
) => {
  await awaitBeiyingRemappingReadyForLayout(beiyingKeyboard);

  let equipmentData: any = localStorage.getItem("equipmentData");
  let keyboardType: any = "";
  if (equipmentData) {
    equipmentData = JSON.parse(equipmentData);
    keyboardType = equipmentData.keyboardType;
  }
  const layoutType = resolveBeiyingLayoutType(equipmentData);
  const beforeKeyData = KEYBOARD_LAYOUTS[layoutType];
  if (!beforeKeyData?.length) {
    throw new Error(
      `[getInitializeKeyboard] 未找到键盘布局：keyboardType="${keyboardType || "(空)"}" layoutType="${layoutType}"`
    );
  }
  const rows: SDKRowLayout[] = [];
  const resultKeyData: any = [];

  if (typeof beiyingKeyboard.instance?.getKeyboardKeys === "function") {
    const rawKeys = await beiyingKeyboard.instance.getKeyboardKeys({
      layer: Number(layer) || 0,
    });
    const keys = Array.isArray(rawKeys) ? rawKeys : [];
    const keyByPos = new Map<number, number>();
    for (const item of keys) {
      const pos = Number(item?.pos);
      const value = Number(item?.value);
      if (!Number.isFinite(pos) || !Number.isFinite(value)) continue;
      keyByPos.set(pos, value);
    }
    if (layoutType === "X98_1K") {
      const hidToPos = getX98_1kMergedHidToPosMapFromDeviceKeys(keys);
      const posToMacro = build8815PosToMacroIndexMap(keys);
      const machineLayer = Number(layer) || 0;
      for (let i = 0; i < beforeKeyData.length; i++) {
        const rowKeys = beforeKeyData[i];
        const mappedRow: number[] = [];
        for (let j = 0; j < rowKeys.length; j++) {
          const layoutCode = rowKeys[j];
          if (layoutCode === 0) {
            mappedRow.push(0);
            continue;
          }
          mappedRow.push(
            resolveX98815InitializeCell(
              layoutCode,
              machineLayer,
              posToMacro,
              hidToPos,
              keyByPos
            )
          );
        }
        rows.push({
          layer,
          row: i,
          keyboardLayout: mappedRow,
        });
      }
      return rows;
    }
    /**
     * M71：keyByPos 的键为设备物理 pos；按 pos 升序与非 0 矩阵格顺序对齐后取 value（与改键 layout 矩阵解耦）。
     */
    if (layoutType === "m71") {
      const sortedPos = [
        ...new Set(
          keys
            .map((k: any) => Number(k?.pos))
            .filter((p: number) => Number.isFinite(p))
        ),
      ].sort((a, b) => a - b);
      let idx = 0;
      for (let i = 0; i < beforeKeyData.length; i++) {
        const rowKeys = beforeKeyData[i];
        const mappedRow: number[] = [];
        for (let j = 0; j < rowKeys.length; j++) {
          const layoutCode = rowKeys[j];
          if (layoutCode === 0) {
            mappedRow.push(0);
            continue;
          }
          const pos = sortedPos[idx];
          idx += 1;
          const fromPos =
            pos !== undefined && Number.isFinite(pos)
              ? (keyByPos.get(pos) ?? keyByPos.get(pos + 1) ?? null)
              : null;
          mappedRow.push(fromPos ?? layoutCode);
        }
        rows.push({
          layer,
          row: i,
          keyboardLayout: mappedRow,
        });
      }
      return rows;
    }
    let sequentialIndex = 0;
    for (let i = 0; i < beforeKeyData.length; i++) {
      const rowKeys = beforeKeyData[i];
      const mappedRow: number[] = [];
      for (let j = 0; j < rowKeys.length; j++) {
        const layoutCode = rowKeys[j];
        if (layoutCode === 0) {
          mappedRow.push(0);
          continue;
        }
        const fromPos =
          keyByPos.get(sequentialIndex) ??
          keyByPos.get(sequentialIndex + 1) ??
          null;
        mappedRow.push(
          layoutType === "es87"
            ? resolveEs87LayoutCellDisplayValue(layoutCode, fromPos)
            : (fromPos ?? layoutCode)
        );
        sequentialIndex += 1;
      }
      rows.push({
        layer,
        row: i,
        keyboardLayout: mappedRow,
      });
    }
    return rows;
  }

  const byArr: any = [];
  beforeKeyData.map((item: any) => {
    item.map((items: any) => {
      if (items !== 0) {
        const byData: any = KEYCODE_MAP_BEIYING.find(
          (byItem: any) =>
            byItem.keycode === items || byItem.xsKeycode === items
        );
        byArr.push(byData?.id);
      }
    });
  });
  const byParams = {
    layer: layer === 1 ? LayerType.Fn1 : LayerType.Normal,
    system: Number(system) === 1 ? SystemType.MacOS : SystemType.Windows,
    ids: byArr.map((key: any) => key),
  };
  const fullGetKeymaps =
    beiyingKeyboard.instance?.remapping?.getKeymaps?.(byParams);
  const afterKeyData = await withTimeout(
    Promise.resolve(fullGetKeymaps),
    25_000,
    "beiying getKeymaps(full layout)"
  );
  if (!Array.isArray(afterKeyData)) {
    throw new Error(
      "[getInitializeKeyboard] getKeymaps 未返回数组，设备可能未连接完成"
    );
  }
  let k = 0;

  for (let i = 0; i < beforeKeyData.length; i++) {
    const bkdItem = beforeKeyData[i];
    resultKeyData.push([]);
    for (let j = 0; j < bkdItem.length; j++) {
      const bkdItems = bkdItem[j];
      if (bkdItems !== 0) {
        const deviceKeycode = afterKeyData[k]?.keycode;
        const d: any =
          KEYCODE_MAP_BEIYING.find(
            (byItem: any) => byItem.keycode === deviceKeycode
          ) ||
          MOUSE_KEYS_BEIYING.find(
            (byItem: any) => byItem.keycode === deviceKeycode
          ) ||
          MEDIA_KEYS_BEIYING.find(
            (byItem: any) => byItem.keycode === deviceKeycode
          );
        // console.log("dddddddddddddddd", d);

        // 宏数据匹配获取
        let macroKeycode: any = "";
        const beiyingMacroKeyData = localStorage.getItem("beiyingMacroKeyData"); // 从缓存拿宏keycode
        if (beiyingMacroKeyData) {
          // 缓存有数据
          JSON.parse(beiyingMacroKeyData)?.map((item: any, key: number) => {
            if (deviceKeycode === item) {
              Object.entries(KEYCODE_MAP).forEach(([keyStr, info]) => {
                if (info.index === key) {
                  macroKeycode = keyStr;
                }
              });
            }
          });
        }
        let cellValue = macroKeycode
          ? macroKeycode
          : d?.xsKeycode || d?.keycode;
        if (layoutType === "es87" && !macroKeycode) {
          cellValue = resolveEs87LayoutCellDisplayValue(bkdItems, cellValue);
        }

        // Fn 层厂商功能码常不在 KEYCODE_MAP_*；回退原始键码，避免 undefined
        // 导致 useKeyRemapping 因 originalKeyCode === null 无法改键（ES98 / X98 8K 等 web-hid）
        if (
          (cellValue === undefined || cellValue === null || cellValue === "") &&
          deviceKeycode !== undefined &&
          deviceKeycode !== null
        ) {
          cellValue = deviceKeycode;
        }

        resultKeyData[i].push(cellValue);
        k += 1;
      } else {
        resultKeyData[i].push(0);
      }
    }
    rows.push({
      layer,
      row: i,
      keyboardLayout: resultKeyData[i],
    });
  }
  return rows;
};

export {
  type Beiying8815GetKeyboardKeyItem,
  type X98_1KKeySlot,
  BEIYING_8815_LAYER0_KEYBOARD_KEYS,
  X98_1K_8815_FACTORY_KEY_ENTRIES,
  X98_1K_8815_FACTORY_POS_TO_KEYCODE,
  X98_1K_DEFAULT_POS_BY_KEYCODE,
} from "./x98_1kBeiyingKeyboardRaw";
